import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Group, Select, Stack, Text, TextInput, Textarea } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { AppModal } from '../../shared/components/AppModal';
import { ApiError } from '../../shared/api/problem';
import { useCurrentWarehouseId } from '../warehouses/currentWarehouse';
import { listPurchaseOrders } from '../purchaseOrders/api';
import { createReceipt, type ReceiptDto } from './api';

interface Props {
  opened: boolean;
  onClose: () => void;
  onCreated: (receipt: ReceiptDto) => void;
}

/**
 * Starting a receipt: the two things that fix what it is, plus the delivery note in the driver's
 * hand while somebody is holding it.
 *
 * No batch number field — it comes from the RECEIPT_BATCH sequence at creation.
 *
 * The order list is narrowed to orders that have actually been sent to a supplier. An order still
 * being built has not been placed with anybody, so nothing can have arrived against it, and the
 * server refuses it (53813); offering the choice only to reject it is a worse way to explain the
 * rule than not offering it. The warehouse narrows the list too, for the same reason (53812).
 */
export function NewReceiptModal({ opened, onClose, onCreated }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [purchaseOrderId, setPurchaseOrderId] = useState('');
  const [packingSlipNumber, setPackingSlipNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // A receipt lands in the warehouse the session is working in. There was a picker here; it had
  // to go, because the API refuses a body naming a warehouse other than the header's — and
  // rightly: receiving into a warehouse you are not in is how stock ends up somewhere nobody
  // looks for it.
  const warehouseId = useCurrentWarehouseId();

  const orders = useQuery({
    queryKey: ['purchase-orders', 'receivable', warehouseId || 'none'] as const,
    queryFn: ({ signal }) => listPurchaseOrders({ warehouseId, openOnly: true }, signal),
    enabled: opened && Boolean(warehouseId),
  });

  // openOnly also returns orders still being built. Those cannot be received against.
  const receivable = useMemo(
    () =>
      (orders.data ?? []).filter(
        (o) => o.status === 'issued' || o.status === 'partially_received',
      ),
    [orders.data],
  );

  useEffect(() => {
    if (!opened) return;
    setPurchaseOrderId('');
    setPackingSlipNumber('');
    setNotes('');
    setErrors({});
    setSubmitError(null);
  }, [opened]);

  const mutation = useMutation({
    mutationFn: () =>
      createReceipt({
        warehouseId,
        purchaseOrderId,
        packingSlipNumber: packingSlipNumber.trim() || null,
        notes: notes.trim() || null,
      }),
    onSuccess: (receipt) => {
      void queryClient.invalidateQueries({ queryKey: ['receipts'] });
      onCreated(receipt);
      onClose();
    },
    onError: (err) => {
      if (!(err instanceof ApiError)) {
        setSubmitError(t('receipts.errors.createFailed'));
        return;
      }
      // The refusals here name the order and say what to do — "Issue it to the supplier before
      // receiving against it". Restating them generically drops the only actionable part.
      setSubmitError(err.problem.detail ?? err.problem.title ?? t('receipts.errors.createFailed'));
    },
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    const next: Record<string, string> = {};
    if (!warehouseId) next.warehouseId = t('receipts.errors.warehouseRequired');
    if (!purchaseOrderId) next.purchaseOrderId = t('receipts.errors.orderRequired');
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    mutation.mutate();
  }

  const nothingToReceive = Boolean(warehouseId) && orders.isSuccess && receivable.length === 0;

  return (
    <AppModal opened={opened} onClose={onClose} title={t('receipts.new')} width="md">
      <form onSubmit={onSubmit} noValidate>
        <Stack gap="md">

          <Select
            label={t('receipts.purchaseOrder')}
            description={t('receipts.purchaseOrderHint')}
            placeholder={
              warehouseId ? t('receipts.orderPlaceholder') : t('receipts.pickWarehouseFirst')
            }
            data={receivable.map((o) => ({
              value: o.purchaseOrderId,
              label: `${o.poNumber} — ${o.supplierName}`,
            }))}
            value={purchaseOrderId || null}
            onChange={(v) => setPurchaseOrderId(v ?? '')}
            error={errors.purchaseOrderId}
            disabled={!warehouseId || orders.isLoading}
            searchable
            allowDeselect={false}
            required
          />

          {nothingToReceive && (
            <Alert color="yellow" variant="light" icon={<IconAlertCircle size={16} />}>
              {t('receipts.nothingToReceive')}
            </Alert>
          )}

          <TextInput
            label={t('receipts.packingSlip')}
            description={t('receipts.packingSlipHint')}
            value={packingSlipNumber}
            onChange={(e) => setPackingSlipNumber(e.currentTarget.value)}
            maxLength={50}
          />

          <Textarea
            label={t('receipts.notes')}
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
            autosize
            minRows={2}
            maxRows={5}
          />

          <Text size="xs" c="dimmed">
            {t('receipts.numberAllocated')}
          </Text>

          {submitError && (
            <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
              {submitError}
            </Alert>
          )}

          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose}>
              {t('shell.cancel')}
            </Button>
            <Button type="submit" color="invetrak" loading={mutation.isPending}>
              {t('receipts.create')}
            </Button>
          </Group>
        </Stack>
      </form>
    </AppModal>
  );
}
